import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Footer } from '../../components/Footer/Footer';
import { StepProps } from '../../components/OrionRiskToleranceWorkflow/OrionRiskToleranceWorkflow.meta';
import { StepId } from '../../api/useRiskToleranceWorkflowQuery';
import { VisualPickerContainer } from '../../../CommonComponents/OPL/VisualPicker/VisualPickerContainer';
import { StepCard } from '../../components/StepCard/StepCard';
import { Content } from '../../components/Content/Content';
import { FormValues, options, title } from './InvestmentObjective.meta';
import {
  currentClientSelector,
  isConsumerArea as selectIsConsumerArea,
} from '../../../CommonSelectors/CommonAreaSelector';
import { CONSUMER_AREA_ACCESS_WORKFLOWS } from '../../../CommonConstants';

export const InvestmentObjective: React.FC<StepProps> = ({
  answers,
  onPrevious,
  onNext,
  onSubmit,
  progress,
}) => {
  const currentClient = useSelector(currentClientSelector);
  const isConsumerArea = useSelector(selectIsConsumerArea);

  const { handleSubmit, setValue, control } = useForm<FormValues>({
    defaultValues: {
      [StepId.InvestmentObjective]: answers.investmentObjective,
    },
  });

  const onSubmitStep = ({ investmentObjective }: FormValues) => {
    onSubmit({ stepId: StepId.InvestmentObjective, answer: investmentObjective });
    onNext();
  };

  return (
    <Box minWidth={864}>
      <form onSubmit={handleSubmit(onSubmitStep)}>
        <Content>
          <StepCard title={title}>
            <Controller
              control={control}
              name={StepId.InvestmentObjective}
              render={({ field: { value: formValue } }) => (
                <Box
                  display="flex"
                  flexDirection="row"
                  gridGap="16"
                  role="radiogroup"
                  aria-label={title}
                >
                  {options.map(({ label, value, description, Illustration }, index) => {
                    const checked = value === formValue;

                    return (
                      <VisualPickerContainer
                        key={value}
                        onChange={() => setValue(StepId.InvestmentObjective, value)}
                        checked={checked}
                        css="flex: 1"
                      >
                        <Box display="flex" flexDirection="column">
                          <Illustration />

                          <Text
                            color={checked ? 'primary.600' : 'neutral.700'}
                            textAlign="center"
                            fontWeight={500}
                            mb="4"
                          >
                            {label}
                          </Text>

                          <Text
                            textAlign="center"
                            fontSize="small"
                            color={checked ? 'primary.600' : 'neutral.600'}
                            height={72}
                            mb="16"
                            px={index > 0 ? '8' : 0}
                          >
                            {description}
                          </Text>
                        </Box>
                      </VisualPickerContainer>
                    );
                  })}
                </Box>
              )}
            />
          </StepCard>
        </Content>

        <Footer
          isFirstStep
          onPrevious={onPrevious}
          progress={progress}
          showPrevious={
            !isConsumerArea ||
            currentClient.ConsumerAccessWorkflow === CONSUMER_AREA_ACCESS_WORKFLOWS.ALL_WORKFLOWS
          }
        />
      </form>
    </Box>
  );
};
